import { useState } from 'react';
import { Download, Upload, Database, FileText, AlertTriangle, Trash2 } from 'lucide-react';

export const DataManager = ({ onExport, onImport, onBackup, onRestore, onReset }) => {
  const [importFile, setImportFile] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importError, setImportError] = useState(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  const handleExport = async () => {
    try {
      const data = await onExport();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `baby-sleep-data-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  const handleImport = async () => {
    if (!importFile) return;

    setIsImporting(true);
    setImportError(null);

    try {
      const text = await importFile.text();
      const data = JSON.parse(text);
      
      // Validate data structure
      if (!data.children || !data.sleepEntries) {
        throw new Error('Ungültiges Dateiformat. Bitte wähle eine gültige Export-Datei aus.');
      }

      await onImport(data);
      setImportFile(null);
    } catch (error) {
      setImportError(error.message);
    } finally {
      setIsImporting(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'application/json') {
      setImportFile(file);
      setImportError(null);
    } else {
      setImportError('Bitte wähle eine gültige JSON-Datei aus.');
    }
  };

  const handleReset = async () => {
    setIsResetting(true);
    try {
      await onReset();
      setShowResetConfirm(false);
    } catch (error) {
      console.error('Reset failed:', error);
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header Card */}
      <div className="card bg-gradient-to-r from-primary-50 to-blue-50 dark:from-primary-900/20 dark:to-blue-900/20">
        <div className="flex items-center space-x-3">
          <Database className="w-6 h-6 text-primary-500" />
          <h2 className="text-responsive-lg font-semibold text-gray-900 dark:text-white">
            Daten verwalten
          </h2>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
          Export, Import und Backup deiner Schlafdaten
        </p>
      </div>

      {/* Export Section */}
      <div className="card">
        <div className="flex items-center space-x-3 mb-4">
          <Download className="w-5 h-5 text-green-500" />
          <h3 className="font-medium text-gray-900 dark:text-white">Daten exportieren</h3>
        </div>
        
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Exportiere alle Schlafdaten und Kinder-Informationen als JSON-Datei für Backup oder Übertragung.
        </p>
        
        <button
          onClick={handleExport}
          className="btn-primary flex items-center space-x-2"
        >
          <FileText className="w-4 h-4" />
          <span>Daten exportieren</span>
        </button>
      </div>

      {/* Import Section */}
      <div className="card">
        <div className="flex items-center space-x-3 mb-4">
          <Upload className="w-5 h-5 text-blue-500" />
          <h3 className="font-medium text-gray-900 dark:text-white">Daten importieren</h3>
        </div>
        
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Importiere zuvor exportierte Daten. <strong>Achtung:</strong> Bestehende Daten werden überschrieben.
        </p>

        <div className="space-y-3">
          <div className="flex items-center space-x-3">
            <input
              type="file"
              accept=".json"
              onChange={handleFileSelect}
              className="flex-1 text-sm text-gray-500 dark:text-gray-400
                        file:mr-4 file:py-2 file:px-4
                        file:rounded-md file:border-0
                        file:text-sm file:font-medium
                        file:bg-primary-50 file:text-primary-700
                        dark:file:bg-primary-900/30 dark:file:text-primary-300
                        hover:file:bg-primary-100 dark:hover:file:bg-primary-900/50
                        transition-colors duration-200"
            />
          </div>

          {importFile && (
            <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
              <FileText className="w-4 h-4" />
              <span>{importFile.name}</span>
            </div>
          )}

          {importError && (
            <div className="flex items-center space-x-2 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-3 rounded-md">
              <AlertTriangle className="w-4 h-4" />
              <span>{importError}</span>
            </div>
          )}

          <button
            onClick={handleImport}
            disabled={!importFile || isImporting}
            className="btn-primary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Upload className="w-4 h-4" />
            <span>{isImporting ? 'Importiere...' : 'Daten importieren'}</span>
          </button>
        </div>
      </div>

      {/* Backup Section */}
      <div className="card">
        <div className="flex items-center space-x-3 mb-4">
          <Database className="w-5 h-5 text-purple-500" />
          <h3 className="font-medium text-gray-900 dark:text-white">Lokales Backup</h3>
        </div>
        
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Erstelle ein lokales Backup im Browser-Speicher oder stelle ein Backup wieder her.
        </p>
        
        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
          <button
            onClick={onBackup}
            className="btn-secondary flex items-center justify-center space-x-2"
          >
            <Download className="w-4 h-4" />
            <span>Backup erstellen</span>
          </button>
          
          <button
            onClick={onRestore}
            className="btn-secondary flex items-center justify-center space-x-2"
          >
            <Upload className="w-4 h-4" />
            <span>Backup wiederherstellen</span>
          </button>
        </div>
      </div>

      {/* Database Reset Section */}
      <div className="card border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20">
        <div className="flex items-center space-x-3 mb-4">
          <Trash2 className="w-5 h-5 text-red-500" />
          <h3 className="font-medium text-red-900 dark:text-red-100">Datenbank zurücksetzen</h3>
        </div>
        
        <p className="text-sm text-red-800 dark:text-red-200 mb-4">
          <strong>Warnung:</strong> Diese Aktion löscht alle Daten unwiderruflich. Stelle sicher, dass du ein Backup erstellt hast.
        </p>
        
        {!showResetConfirm ? (
          <button
            onClick={() => setShowResetConfirm(true)}
            className="btn-danger flex items-center space-x-2"
          >
            <Trash2 className="w-4 h-4" />
            <span>Datenbank zurücksetzen</span>
          </button>
        ) : (
          <div className="space-y-3">
            <div className="bg-red-100 dark:bg-red-900/30 p-3 rounded-md">
              <p className="text-sm text-red-800 dark:text-red-200 font-medium">
                Bist du sicher? Diese Aktion kann nicht rückgängig gemacht werden.
              </p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={handleReset}
                disabled={isResetting}
                className="btn-danger flex items-center space-x-2 disabled:opacity-50"
              >
                <Trash2 className="w-4 h-4" />
                <span>{isResetting ? 'Setze zurück...' : 'Ja, zurücksetzen'}</span>
              </button>
              <button
                onClick={() => setShowResetConfirm(false)}
                disabled={isResetting}
                className="btn-secondary disabled:opacity-50"
              >
                Abbrechen
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Information */}
      <div className="card bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
        <div className="flex items-start space-x-3">
          <AlertTriangle className="w-5 h-5 text-blue-500 mt-0.5" />
          <div>
            <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">
              Wichtige Hinweise
            </h4>
            <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
              <li>• Export-Dateien enthalten alle Daten und können sicher übertragen werden</li>
              <li>• Lokale Backups werden automatisch alle 5 Minuten erstellt</li>
              <li>• Bei Import werden alle bestehenden Daten überschrieben</li>
              <li>• Daten werden nur lokal gespeichert und nicht an Server übertragen</li>
              <li>• Bei Datenbankproblemen kann ein Reset helfen, aber löscht alle Daten</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}; 